import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { tryParseJson } from "@/lib/utils";
import { useApiUrl, useDataProvider, useGo } from "@refinedev/core";
import { RotateCcw } from "lucide-react";
import React from "react";

function getEnvBaseUrl() {
  return import.meta.env.VITE_ENV === "dev"
    ? import.meta.env.VITE_DEV_BASE_URL
    : import.meta.env.VITE_BASE_URL;
}

export default function SettingsGeneralPage() {
  const { custom } = useDataProvider()();
  const apiUrl = useApiUrl();
  const [defaults, setDefaults] = React.useState<any>(
    tryParseJson(localStorage.getItem("defaults")),
  );
  const [urlInput, setUrlInput] = React.useState(
    localStorage.getItem("apiBaseUrl") || "",
  );
  const isOverridden = !!localStorage.getItem("apiBaseUrl");

  const go = useGo();

  return (
    <div className="space-y-6 text-foreground">
      <div>
        <h3 className="text-lg font-medium">General</h3>
        <p className="text-sm text-muted-foreground">
          Update general settings. Set your defaults.
        </p>
      </div>

      <Separator className="bg-foreground/10" />

      {/* API URL */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">API Base URL</h4>
        <p className="text-xs text-muted-foreground">
          Current:{" "}
          <code className="rounded bg-muted px-1 py-0.5">{apiUrl}</code>
          {isOverridden && (
            <span className="ml-2 text-amber-500 font-medium">(overridden)</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          Default ({import.meta.env.VITE_ENV}):{" "}
          <code className="rounded bg-muted px-1 py-0.5">{getEnvBaseUrl()}</code>
        </p>
        <div className="flex items-end gap-2">
          <Input
            className="flex-1 font-mono text-sm"
            placeholder={getEnvBaseUrl()}
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
          />
          <Button
            type="button"
            onClick={() => {
              const trimmed = urlInput.trim().replace(/\/+$/, "");
              if (trimmed) {
                localStorage.setItem("apiBaseUrl", trimmed);
              } else {
                localStorage.removeItem("apiBaseUrl");
              }
              window.location.reload();
            }}
          >
            Apply
          </Button>
          {isOverridden && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                localStorage.removeItem("apiBaseUrl");
                setUrlInput("");
                window.location.reload();
              }}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}
        </div>
      </div>

      <Separator className="bg-foreground/10" />

      {/* Default City */}
      <div className="flex items-end space-x-2 w-full">
        <RemoteSelect
          parentClassName="w-full"
          value={defaults?.geodata}
          label="Default City"
          onValueChanged={async (value) => {
            const newDefaults = { ...defaults, geodata: value };
            await new Promise((resolve) =>
              setTimeout(() => {
                custom?.({
                  url: `https://wft-geo-db.p.rapidapi.com/v1/geo/cities/${value.id}`,
                  method: "get",
                  headers: {
                    "x-rapidapi-host": "wft-geo-db.p.rapidapi.com",
                    "x-rapidapi-key":
                      "74d6a4de7cmsh9ea971ad83b0534p1bf0a6jsn8eb989a44d8c",
                  },
                })
                  .then((response) => response.data?.data)
                  .then((data) => {
                    const tz = data?.timezone?.replace("__", "/");

                    newDefaults.timezone = tz;
                    resolve(newDefaults);
                  })
                  .catch((error) => {
                    console.error(error);
                    resolve(newDefaults);
                  });
              }, 1000),
            );
            setDefaults(newDefaults);
          }}
          accessorFn={(option) => ({
            id: option.id?.toString(),
            name: `${option.name} (${option.countryCode})`,
          })}
          onSearch={async (query, onStatusChanged) => {
            if (!query) {
              return [];
            }

            onStatusChanged("loading");
            const response = await custom?.({
              url: "https://wft-geo-db.p.rapidapi.com/v1/geo/places",
              method: "get",
              headers: {
                "x-rapidapi-host": "wft-geo-db.p.rapidapi.com",
                "x-rapidapi-key":
                  "74d6a4de7cmsh9ea971ad83b0534p1bf0a6jsn8eb989a44d8c",
              },
              query: {
                namePrefix: query,
                types: "ISLAND, CITY",
                sort: "-population",
                limit: 10,
              },
            });

            onStatusChanged("success");

            if (!response?.data) {
              return [];
            }

            return response?.data?.data?.map((item: any) => item);
          }}
        />
        <Button
          className="w-1/4"
          type="button"
          onClick={() => {
            localStorage.setItem("defaults", JSON.stringify(defaults));
          }}
        >
          Set default
        </Button>
        <Button
          className="w-1/4"
          type="button"
          variant={"secondary"}
          onClick={() => {
            const path = go({
              to: "/events/create",
              query: {
                defaults: JSON.stringify(defaults),
              },
              type: "path",
            });
            if (path) window.open(path, "_blank");
          }}
        >
          Get url
        </Button>
      </div>
    </div>
  );
}
